import React, { useEffect, useState } from "react";
import {
  Card,
  CardContent,
  Typography,
  Grid,
  CircularProgress,
  Snackbar,
  Alert,
  Avatar,
  Box,
  Container,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import axios from "axios";
import { baseURL, config, requestID } from "../../../common/utils";
import {
  IonContent,
  IonHeader,
  IonPage,
  IonTitle,
  IonToolbar,
} from "@ionic/react";

function WinnersPage() {
  const [electionName, setElectionName] = useState("");
  const [electionId, setElectionId] = useState("");
  const [loading, setLoading] = useState(true);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const [results, setResults] = useState([]);
  const [showResults, setShowResults] = useState(false);

  useEffect(() => {
    fetchElectionData();
    fetchElectionResults();

    const interval = setInterval(() => {
      fetchElectionData();
    }, 180000);

    return () => clearInterval(interval);
  }, []);

  const fetchElectionResults = async () => {
    try {
      const payload = {
        requestID: requestID(),
        election_id: electionId,
      };

      const response = await axios.post(
        `${baseURL}elections/getElectionResult`,
        payload,
        config
      );
      console.log(response);

      if (!response.data.status) {
        throw new Error(response.data.message || "No results available");
      }

      setResults(response.data.positions);
      setShowResults(true);
    } catch (error) {
      setSnackbar({
        open: true,
        message: error.message || "Failed to fetch election results",
        severity: "error",
      });
    }
  };

  const fetchElectionData = async () => {
    try {
      setLoading(true);
      const payload = {
        requestID: requestID(),
        election_id: electionId,
      };
      const response = await axios.post(
        `${baseURL}elections/getAspirantsByPosition`,
        payload,
        config
      );
      console.log(response);
      if (!response.data.status) {
        throw new Error(response.data.message || "No active election found");
      }

      setElectionName(response.data.election_name);
      setElectionId(response.data.election_id);
    } catch (error) {
      setSnackbar({
        open: true,
        message: error.message || "Failed to fetch election data",
        severity: "error",
      });
    } finally {
      setLoading(false);
    }
  };
  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="60vh"
      >
        <CircularProgress size={60} />
      </Box>
    );
  }

  return (
    <IonPage>
      <IonHeader>
        <IonToolbar>
          <IonTitle>Voting Results</IonTitle>
        </IonToolbar>
      </IonHeader>
      <IonContent fullscreen>
        <Container
          maxWidth="lg"
          sx={{
            height: "100vh",
            overflow: "hidden",
            display: "flex",
            flexDirection: "column",
            p: isMobile ? 1 : 2,
          }}
        >
          <Box
            sx={{
              py: 2,
              position: "sticky",
              top: 0,
              backgroundColor: "background.paper",
              zIndex: theme.zIndex.appBar,
              boxShadow: theme.shadows[1],
            }}
          >
            <Typography
              variant="h4"
              align="center"
              sx={{
                fontWeight: "bold",
                color: "primary.main",
                mb: 1,
              }}
            >
              {electionName}
            </Typography>
          </Box>

          <Box
            sx={{
              flex: 1,
              overflowY: "auto",
              py: 1,
              "&::-webkit-scrollbar": {
                width: "6px",
              },
              "&::-webkit-scrollbar-track": {
                background: theme.palette.grey[100],
              },
              "&::-webkit-scrollbar-thumb": {
                background: theme.palette.primary.main,
                borderRadius: "3px",
              },
            }}
          >
            {results.length === 0 ? (
              <Box
                display="flex"
                justifyContent="center"
                alignItems="center"
                minHeight="50vh"
              >
                <Typography variant="h6" color="textSecondary">
                  No Active Election
                </Typography>
              </Box>
            ) : (
              results.map((pos, index) => {
                return (
                  <Card
                    key={index}
                    sx={{
                      mb: 3,
                      borderRadius: 2,
                      boxShadow: theme.shadows[2],
                      "&:hover": { boxShadow: theme.shadows[4] },
                    }}
                  >
                    <CardContent>
                      <Typography
                        variant="h5"
                        sx={{
                          fontWeight: "bold",
                          color: "primary.main",
                          mb: 2,
                          pb: 1,
                          borderBottom: `1px solid ${theme.palette.divider}`,
                        }}
                      >
                        {pos.position_name}
                      </Typography>
                      <Grid container spacing={2}>
                        {pos.winners.map((aspirant: any) => (
                          <Grid item xs={12} sm={6} md={4} key={aspirant.id}>
                            <Card
                              variant="outlined"
                              sx={{
                                height: "100%",
                                display: "flex",
                                flexDirection: "column",
                                transition: "transform 0.2s",
                                "&:hover": { transform: "translateY(-4px)" },
                              }}
                            >
                              <CardContent
                                sx={{
                                  display: "flex",
                                  flexDirection: "column",
                                  alignItems: "center",
                                  flexGrow: 1,
                                }}
                              >
                                <Avatar
                                  src={aspirant.photo_url || ""}
                                  sx={{
                                    width: 80,
                                    height: 80,
                                    mb: 2,
                                    fontSize: "2.5rem",
                                    backgroundColor:
                                      theme.palette.primary.light,
                                  }}
                                />
                                <Typography
                                  variant="h6"
                                  align="center"
                                  gutterBottom
                                  sx={{ fontWeight: 600 }}
                                >
                                  {aspirant.full_name}
                                </Typography>
                                {aspirant.party && (
                                  <Typography
                                    variant="subtitle2"
                                    color="textSecondary"
                                    gutterBottom
                                  >
                                    {aspirant.user_id}
                                  </Typography>
                                )}
                                <Typography
                                  variant="body2"
                                  color="primary"
                                  sx={{ mt: 1 }}
                                >
                                  {aspirant.total_votes} votes
                                </Typography>
                              </CardContent>
                            </Card>
                          </Grid>
                        ))}
                      </Grid>
                    </CardContent>
                  </Card>
                );
              })
            )}
          </Box>

          <Snackbar
            open={snackbar.open}
            autoHideDuration={6000}
            onClose={() => setSnackbar({ ...snackbar, open: false })}
            anchorOrigin={{ vertical: "top", horizontal: "center" }}
          >
            <Alert
              severity={snackbar.severity}
              onClose={() => setSnackbar({ ...snackbar, open: false })}
              sx={{ width: "100%" }}
            >
              {snackbar.message}
            </Alert>
          </Snackbar>
        </Container>
      </IonContent>
    </IonPage>
  );
}

export default WinnersPage;
